import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { MessageCircle, X } from 'lucide-react';
import { useSocket } from '../../contexts/SocketContext';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import GlobalChatTab from './GlobalChatTab';
import AnonymousChatTab from './AnonymousChatTab';
import RoomChatTab from './RoomChatTab';
import PepeChatTab from './PepeChatTab';
import StoreChatTab from './StoreChatTab';
import TransportChatTab from './TransportChatTab';
import './UnifiedChat.css';

const UnifiedChat = ({ activeTransportRequest }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('global');
  const [showRoomTab, setShowRoomTab] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [hasOpenedInRoom, setHasOpenedInRoom] = useState(false);
  const [storeCustomerNotify, setStoreCustomerNotify] = useState(false);
  const [storeStaffNotify, setStoreStaffNotify] = useState(false);
  const [staffStores, setStaffStores] = useState([]);
  const [selectedStoreSlug, setSelectedStoreSlug] = useState(null);
  const [forceStorePicker, setForceStorePicker] = useState(false);
  
  const { socket } = useSocket();
  const { user } = useAuth();
  const location = useLocation();

  const prevIsOpenRef = useRef(false);
  const didAutoSwitchToStoreRef = useRef(false);

  const storeMatch = location.pathname.match(/^\/store\/([^/]+)/);
  const storeSlug = storeMatch ? storeMatch[1] : null;

  useEffect(() => {
    if (!storeSlug) return;
    setSelectedStoreSlug(storeSlug);
  }, [storeSlug]);

  useEffect(() => {
    if (!user) {
      setStaffStores([]);
      return;
    }

    let cancelled = false;

    const fetchStaffStores = async () => {
      try {
        const response = await axios.get(`/api/admin/store-staff/user/${user.id}`);
        if (cancelled) return;
        const listRaw = Array.isArray(response?.data) ? response.data : [];
        const active = listRaw.filter((row) => row && row.is_active !== false);
        setStaffStores(active);
      } catch (e) {
        if (!cancelled) {
          setStaffStores([]);
        }
      }
    };

    fetchStaffStores();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (selectedStoreSlug) return;
    const firstSlug = staffStores[0]?.store_slug || null;
    if (firstSlug) {
      setSelectedStoreSlug(firstSlug);
    }
  }, [staffStores, selectedStoreSlug]);

  const effectiveStoreSlug = useMemo(() => {
    if (storeSlug) return storeSlug;
    if (selectedStoreSlug) return selectedStoreSlug;
    return null;
  }, [storeSlug, selectedStoreSlug]);

  const canPickStore = !!user && staffStores.length > 1;
  const activeTransportStatuses = ['accepted', 'arrived_at_store', 'picked_up', 'delivered_pending'];

  const canShowTransportTab =
    !!activeTransportRequest &&
    activeTransportRequest.kind === 'ride' &&
    activeTransportStatuses.includes(activeTransportRequest.status);

  // Cuando el chat de tienda está abierto, limpiamos notificaciones de tienda
  useEffect(() => {
    if (isOpen && activeTab === 'store') {
      setStoreCustomerNotify(false);
      setStoreStaffNotify(false);
    }
  }, [isOpen, activeTab]);

  // Detectar si usuario está en una sala
  useEffect(() => {
    const path = location.pathname;
    
    // TicTacToe Room
    const tttMatch = path.match(/\/tictactoe\/room\/(\d{6})/);
    if (tttMatch) {
      setCurrentRoom(prev => {
        if (prev?.type === 'tictactoe' && prev?.code === tttMatch[1]) return prev;
        return { type: 'tictactoe', code: tttMatch[1] };
      });
      setShowRoomTab(true);
      return;
    }
    
    // Bingo Room
    const bingoMatch = path.match(/\/bingo\/v2\/(play|room)\/(\d{6})/);
    if (bingoMatch) {
      setCurrentRoom(prev => {
        if (prev?.type === 'bingo' && prev?.code === bingoMatch[2]) return prev;
        return { type: 'bingo', code: bingoMatch[2] };
      });
      setShowRoomTab(true);
      return;
    }
    
    // No está en sala - ocultar pestaña Sala
    if (showRoomTab) {
      setShowRoomTab(false);
      if (activeTab === 'room') {
        setActiveTab('global');
      }
    }
    setCurrentRoom(null);
  }, [location.pathname, showRoomTab, activeTab]);

  // Cambiar a pestaña 'room' solo cuando se ABRE el chat estando en una sala
  useEffect(() => {
    if (isOpen && showRoomTab && !hasOpenedInRoom) {
      setActiveTab('room');
      setHasOpenedInRoom(true);
    }
    
    // Resetear flag cuando se cierra el chat
    if (!isOpen) {
      setHasOpenedInRoom(false);
    }
  }, [isOpen, showRoomTab, hasOpenedInRoom]);

  // Cuando se abre el chat dentro de una tienda y no hay sala, empezar en pestaña de tienda
  // PERO solo una vez por apertura (no bloquear el cambio manual a Global).
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (!isOpen) {
      didAutoSwitchToStoreRef.current = false;
      return;
    }

    const justOpened = isOpen && !wasOpen;
    if (!justOpened) return;

    if (effectiveStoreSlug && !showRoomTab && !didAutoSwitchToStoreRef.current) {
      setActiveTab('store');
      didAutoSwitchToStoreRef.current = true;
    }
  }, [isOpen, effectiveStoreSlug, showRoomTab]);

  // Si sales de una ruta de tienda, permitir que el próximo open auto-seleccione de nuevo.
  useEffect(() => {
    if (!storeSlug) {
      didAutoSwitchToStoreRef.current = false;
    }
  }, [storeSlug]);

  // Join/Leave room chat al cambiar de sala
  useEffect(() => {
    if (socket && currentRoom) {
      socket.emit('room:join_chat', {
        roomType: currentRoom.type,
        roomCode: currentRoom.code
      });
      
      return () => {
        socket.emit('room:leave_chat', {
          roomType: currentRoom.type,
          roomCode: currentRoom.code
        });
      };
    }
  }, [socket, currentRoom]);

  useEffect(() => {
    if (!canShowTransportTab && activeTab === 'transport') {
      setActiveTab('global');
    }
  }, [canShowTransportTab, activeTab]);

  const handleStoreCustomerNew = useCallback(() => {
    setStoreCustomerNotify(true);
  }, []);

  const handleStoreStaffNew = useCallback(() => {
    setStoreStaffNotify(true);
  }, []);

  return (
    <div className={`unified-chat ${isOpen ? 'open' : 'minimized'}`}>
      {/* Botón Icono (50% tamaño original) */}
      <button 
        className={`chat-toggle-btn ${
          storeCustomerNotify ? 'store-notify-client' : storeStaffNotify ? 'store-notify-staff' : ''
        }`}
        onClick={() => setIsOpen(!isOpen)}
        title="Chat"
      >
        <MessageCircle size={20} />
      </button>

      {isOpen && (
        <div className="chat-container">
          {/* Header con Pestañas */}
          <div className="chat-header">
            <div className="chat-tabs">
              <button
                className={`tab ${activeTab === 'global' ? 'active' : ''}`}
                onClick={() => setActiveTab('global')}
              >
                🌍
              </button>
              
              <button
                className={`tab ${activeTab === 'anonymous' ? 'active' : ''}`}
                onClick={() => setActiveTab('anonymous')}
              >
                👤
              </button>
              
              <button
                className={`tab ${activeTab === 'pepe' ? 'active' : ''}`}
                onClick={() => setActiveTab('pepe')}
                title="Chat con Pepe (IA)"
              >
                🤖
              </button>
              
              {effectiveStoreSlug && (
                <button
                  className={`tab ${activeTab === 'store' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab('store');
                    if (canPickStore) {
                      setForceStorePicker(true);
                    }
                  }}
                  title="Chat con la tienda"
                >
                  🏬
                </button>
              )}
              
              {showRoomTab && (
                <button
                  className={`tab ${activeTab === 'room' ? 'active' : ''}`}
                  onClick={() => setActiveTab('room')}
                >
                  🎮
                </button>
              )}

              {canShowTransportTab && (
                <button
                  className={`tab ${activeTab === 'transport' ? 'active' : ''}`}
                  onClick={() => setActiveTab('transport')}
                  title="Chat de Transporte"
                >
                  🚗
                </button>
              )}
            </div>
            
            <button 
              className="close-btn"
              onClick={() => setIsOpen(false)}
            >
              <X size={18} />
            </button>
          </div>

          {/* Contenido de Pestañas */}
          <div className="chat-content">
            {activeTab === 'global' && <GlobalChatTab />}
            {activeTab === 'anonymous' && <AnonymousChatTab />}
            {activeTab === 'pepe' && <PepeChatTab />}
            {activeTab === 'store' && effectiveStoreSlug && (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {canPickStore && (
                  <div style={{ padding: '6px 8px' }}>
                    <select
                      value={selectedStoreSlug || ''}
                      onChange={(e) => {
                        setSelectedStoreSlug(e.target.value || null);
                        setForceStorePicker(false);
                      }}
                      style={{ width: '100%' }}
                    >
                      {staffStores.map((s) => (
                        <option key={s.store_id || s.id} value={s.store_slug}>
                          {s.store_name || s.store_slug}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div style={{ flex: 1, minHeight: 0 }}>
                  {forceStorePicker && canPickStore ? (
                    <div className="store-selector-container">
                      <div className="store-selector-title">
                        Selecciona una tienda
                      </div>
                      <div className="store-selector-subtitle">
                        Elige la tienda para abrir el chat.
                      </div>
                      <div className="store-selector-list">
                        {staffStores.map((s) => (
                          <button
                            key={s.store_id || s.id}
                            type="button"
                            onClick={() => {
                              setSelectedStoreSlug(s.store_slug);
                              setForceStorePicker(false);
                            }}
                            className="store-selector-item"
                          >
                            {s.store_name || s.store_slug}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <StoreChatTab
                      storeSlug={effectiveStoreSlug}
                      onNewCustomerMessage={handleStoreCustomerNew}
                      onNewStaffMessage={handleStoreStaffNew}
                    />
                  )}
                </div>
              </div>
            )}
            {activeTab === 'room' && currentRoom && (
              <RoomChatTab 
                roomType={currentRoom.type}
                roomCode={currentRoom.code}
              />
            )}

            {activeTab === 'transport' && canShowTransportTab && (
              <TransportChatTab request={activeTransportRequest} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedChat;
